import { defineStore } from "pinia";
import {
  createTeam as createTeamRequest,
  deleteTeamById,
  getTeamInfo,
  updateTeamById,
} from "@/api/getInfo";
import { notify } from "@/utils/nativeFeedback";
import {
  createPersistenceConfig,
  readCache,
  writeCache,
  clearCache,
  isCacheAvailable,
  isCacheExpiring,
  getCacheInfo,
  type CacheInfo,
} from "@/utils/cache";

// ========== 持久化配置 ==========
const PERSISTENCE_CONFIG = createPersistenceConfig("teamList");

export type TeamType = {
  advantages: { advantage: string; sortOrder: number }[];
  description: string;
  id: number;
  name: string;
  photographs: { src: string; alt: string; sortOrder: number }[];
};

export type teamType = TeamType;

interface TeamsState {
  teamList: TeamType[];
}

export const useTeamsStore = defineStore("teams", {
  state: (): TeamsState => ({
    teamList: [],
  }),
  getters: {
    getTeamsList: (state) => state.teamList,
  },
  actions: {
    async fetchTeamsList(
      forceRefresh = false,
      silentRefresh = false,
    ): Promise<void> {
      // 1. 检查内存缓存
      if (this.teamList.length > 0 && !forceRefresh) {
        return;
      }

      // 2. 检查 localStorage 缓存
      if (!forceRefresh) {
        const cachedData = readCache<TeamType[]>(PERSISTENCE_CONFIG);

        if (cachedData && Array.isArray(cachedData)) {
          this.teamList = cachedData;

          // 静默刷新：缓存即将过期时后台异步刷新
          if (silentRefresh && isCacheExpiring(PERSISTENCE_CONFIG)) {
            this.refreshInBackground();
          }

          return;
        }
      }

      // 3. 请求接口
      try {
        const response = await getTeamInfo();
        const data = response?.data;

        if (!this.isValidTeamArray(data)) {
          throw new Error("接口返回数据格式异常");
        }

        this.teamList = data;
        writeCache(PERSISTENCE_CONFIG, data);
      } catch (error) {
        // 降级策略
        if (isCacheAvailable(PERSISTENCE_CONFIG)) {
          const fallbackData = readCache<TeamType[]>(PERSISTENCE_CONFIG);
          if (fallbackData && Array.isArray(fallbackData)) {
            this.teamList = fallbackData;
            return;
          }
        }

        if (this.teamList.length === 0) {
          throw error;
        }
      }
    },

    async refreshInBackground(): Promise<void> {
      try {
        const response = await getTeamInfo();
        const data = response?.data;

        if (this.isValidTeamArray(data)) {
          this.teamList = data;
          writeCache(PERSISTENCE_CONFIG, data);
        }
      } catch (error) {}
    },

    async refreshTeams(): Promise<void> {
      return this.fetchTeamsList(true, false);
    },

    clearCache(): void {
      this.teamList = [];
      clearCache(PERSISTENCE_CONFIG);
    },

    getCacheInfo(): CacheInfo {
      return getCacheInfo(PERSISTENCE_CONFIG);
    },

    isValidTeamArray(data: unknown): data is TeamType[] {
      return (
        Array.isArray(data) &&
        data.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "id" in item &&
            typeof (item as TeamType).id === "number",
        )
      );
    },

    updateTeam(updatedTeam: TeamType): void {
      const index = this.teamList.findIndex((t) => t.id === updatedTeam.id);
      if (index !== -1) {
        this.teamList[index] = { ...this.teamList[index], ...updatedTeam };
        writeCache(PERSISTENCE_CONFIG, this.teamList);
      }
    },

    async createTeam(data: Partial<TeamType>): Promise<boolean> {
      try {
        const response = await createTeamRequest(data);
        if (response?.code !== 200) {
          notify(response?.msg || "新增团队失败", "error");
          return false;
        }
        await this.refreshTeams();
        notify("新增成功", "success");
        return true;
      } catch (error) {
        console.error("新增团队失败:", error);
        notify("新增团队失败", "error");
        return false;
      }
    },

    async updateTeamById(id: number, data: Partial<TeamType>): Promise<boolean> {
      try {
        const response = await updateTeamById(id, data);
        if (response?.code !== 200) {
          notify(response?.msg || "更新团队失败", "error");
          return false;
        }
        const current = this.teamList.find((team) => team.id === id);
        if (current) this.updateTeam({ ...current, ...data, id });
        notify("更新成功", "success");
        return true;
      } catch (error) {
        console.error("更新团队失败:", error);
        notify("更新团队失败", "error");
        return false;
      }
    },

    async deleteTeam(id: number): Promise<boolean> {
      try {
        await deleteTeamById(id);
        this.teamList = this.teamList.filter((team) => team.id !== id);
        writeCache(PERSISTENCE_CONFIG, this.teamList);
        notify("删除成功", "success");
        return true;
      } catch (error) {
        console.error("删除团队失败:", error);
        notify("删除团队失败", "error");
        return false;
      }
    },
  },
});
